import Button from 'monday-ui-react-core/dist/Button';
import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactSignatureCanvas from 'react-signature-canvas';
import styled from 'styled-components';
import DeleteIcon from '../../assets/icons/delete.svg';
import CustomButton from '../CustomButton';
import TabsContext from 'monday-ui-react-core/dist/TabsContext';
import TabList from 'monday-ui-react-core/dist/TabList';
import Tab from 'monday-ui-react-core/dist/Tab';
import TabPanels from 'monday-ui-react-core/dist/TabPanels';
import TabPanel from 'monday-ui-react-core/dist/TabPanel';
import { FileContext } from '../../context/FileContext';
import { useTranslation } from 'react-i18next';

const SignatureBodyContainer = styled.div`
  width: 434px;
  height: 202px;
  background-color: #e6e9ef;
  color-scheme: only light;
  forced-color-adjust: none;
  border-radius: 16px;
  position: relative;

  @media screen and (max-width: 768px) {
    width: 100%;
  }

  canvas {
    width: 100%;
    height: 100%;
    overflow-y: auto;
    background-color: #ffffff;
    color-scheme: only light;
    forced-color-adjust: none;
  }

  .input-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    input,
    input:active,
    input:focus-visible {
      border: none;
      background-color: #ffffff;
      color: #000000;
      -webkit-text-fill-color: #000000;
      color-scheme: light;
      cursor: auto;
      height: 90px;
      font-size: 48px;
      line-height: 26px;
      width: 90%;
      text-align: center;
      font-family: 'Pacifico';
      outline: none;
    }
  }

  button.delete {
    position: absolute;
    left: 8px;
    bottom: 8px;

    @media only screen and (max-width: 768px) {
      width: 100% !important;
    }
  }
`;

const ActionSectionContainer = styled.div`
  display: flex;
  flex-direction: row-reverse;

  button.add {
    width: fit-content;
    margin-left: auto;
    margin-top: 12px;

    @media only screen and (max-width: 768px) {
      width: 100% !important;
    }
  }
`;

const SignatureDialogContainer = styled.div`
  position: relative;
  padding: 2rem;
  padding-top: 0;
  display: flex;
  flex-direction: column;
  h1 {
    font-weight: 500;
    font-size: 32px;
    line-height: 38px;
    letter-spacing: -1px;
  }

  @media screen and (min-width: 768px) {
    padding: 3rem;
    padding-top: 0;
    h1 {
      font-size: 32px;
      line-height: 38px;
      margin-bottom: 24px;
    }
  }
`;

const SignatureDialogBody = ({
  handleAdd,
  item,
  action,
  signatureDefault,
  setSignatureDefault,
  initialsDefault,
  setInitialsDefault,
}) => {
  const { t } = useTranslation();
  const sigRef = useRef();
  const textRef = useRef();
  const [activeTab, setActiveTab] = useState(0);
  const [canvasDataUrl, setCanvasDataUrl] = useState('');
  const [isAddButtonDisabled, setIsAddButtonDisabled] = useState(true);

  const dialogType = item?.title?.split(' ')?.[1] || item?.title;

  const { signatureMode, setSignatureMode, triggerToast } =
    useContext(FileContext);

  const handleTouchStart = (event) => {
    event.preventDefault();
    sigRef.current.handleMouseDown(event.touches[0]);
  };

  const handleTouchMove = (event) => {
    event.preventDefault();
    sigRef.current.handleMouseMove(event.touches[0]);
  };

  const handleTextInput = (e) => {
    const value = e.target.value;
    setIsAddButtonDisabled(value ? false : true);
  };

  const handleCanvasInput = () => {
    const empty = sigRef.current.isEmpty();
    setIsAddButtonDisabled(empty);
  };

  const handleDeleteButtonClick = () => {
    if (textRef.current) {
      textRef.current.value = ''; // Clear the input field
      textRef.current.focus();
    }
    sigRef.current?.clear();
    setIsAddButtonDisabled(true);
    const newObj = { mode: null, value: null, id: item.title };
    setSignatureMode((prevArray) =>
      comparePrevDataAndUpdate(prevArray, newObj),
    );
  };

  const handleTabClick = (val) => {
    // handleDeleteButtonClick();
    setActiveTab(val);
  };

  const convertToImage = (item) => {
    if (textRef.current) {
      const input = textRef.current;

      // Set the canvas size to match the input box
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Calculate the text dimensions
      ctx.font = getComputedStyle(input).font;
      const textWidth = ctx.measureText(input?.value).width;
      const textHeight = parseInt(getComputedStyle(input).fontSize, 10) * 2; // Adjust as needed

      // Set the canvas size to match the text dimensions
      canvas.width = textWidth;
      canvas.height = textHeight;

      // Clear the canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw the input box content onto the canvas
      ctx.font = getComputedStyle(input).font;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(input?.value, canvas.width / 2, canvas.height / 2);

      // Convert the canvas to an image data URL
      const generatedImageURL = canvas.toDataURL('image/png');

      handleAdd(generatedImageURL, item, 0.5);
      const newObj = { mode: 'input', value: input?.value, id: item.title };
      setSignatureMode((prevArray) =>
        comparePrevDataAndUpdate(prevArray, newObj),
      );
    }
  };

  const comparePrevDataAndUpdate = (prevArray, newObj) => {
    const existingIndex = prevArray.findIndex((obj) => obj.id === item.title);
    if (existingIndex !== -1) {
      const updatedArray = [...prevArray];
      updatedArray[existingIndex] = newObj;
      return updatedArray;
    } else {
      return [...prevArray, newObj];
    }
  };

  const handleAddDefaultSignatureOrInitials = (mode) => {
    if (dialogType === 'Signature') {
      if (mode === 'input') {
        const input = textRef.current;
        setSignatureDefault({ mode: 'input', value: input?.value });
      }
      if (mode === 'canvas') {
        const canvas = sigRef.current;
        setSignatureDefault({ mode: 'canvas', value: canvas.toDataURL() });
      }
    }
    if (dialogType === 'Initials') {
      if (mode === 'input') {
        const input = textRef.current;
        setInitialsDefault({ mode: 'input', value: input?.value });
      }
      if (mode === 'canvas') {
        const canvas = sigRef.current;
        setInitialsDefault({ mode: 'canvas', value: canvas.toDataURL() });
      }
    }
  };

  useEffect(() => {
    if (textRef.current && sigRef.current) {
      if (action === 'edit') {
        const prevInput = signatureMode?.find((x) => x.id === item.title);
        if (prevInput?.mode === 'input') {
          setActiveTab(1);
          textRef.current.value = prevInput?.value;
          setIsAddButtonDisabled(false);
        } else {
          setCanvasDataUrl(prevInput?.value);
          sigRef.current.fromDataURL(prevInput?.value);
          setIsAddButtonDisabled(false);
        }
      } else {
        // check if both signature and input fields are empty
        if (!textRef.current.value && sigRef.current.isEmpty()) {
          // check if dialog is initials and verify if initials has default value
          if (dialogType === 'Initials' && initialsDefault) {
            // set value accoarding to mode in dialog
            if (initialsDefault?.mode === 'input') {
              setActiveTab(1);
              textRef.current.value = initialsDefault?.value;
            } else {
              setCanvasDataUrl(initialsDefault?.value);
              sigRef.current.fromDataURL(initialsDefault?.value);
            }
            setIsAddButtonDisabled(false);
          }
          // check if dialog is signature and verify if signature has default value
          if (dialogType === 'Signature' && signatureDefault) {
            // set value accoarding to mode in dialog
            if (signatureDefault?.mode === 'input') {
              setActiveTab(1);
              textRef.current.value = signatureDefault?.value;
            } else {
              setCanvasDataUrl(signatureDefault?.value);
              sigRef.current.fromDataURL(signatureDefault?.value);
            }
            setIsAddButtonDisabled(false);
          }
        }
      }
    }
  }, []);

  const isMobile = window.innerWidth <= 600;

  useEffect(() => {
    if (textRef.current && activeTab === 1) {
      textRef.current.focus();
    }
  }, [activeTab]);

  return (
    <SignatureDialogContainer>
      <h1 className="pb-20">
        {t('Add your')} {t(dialogType)}
      </h1>
      <TabsContext activeTabId={activeTab}>
        <TabList tabType={isMobile ? 'stretched' : ''}>
          <Tab value="0" onClick={(val) => handleTabClick(val)}>
            {t('Sign')}
          </Tab>
          <Tab value="1" onClick={(val) => handleTabClick(val)}>
            {t('Type')}
          </Tab>
        </TabList>
        <TabPanels animationDirection={TabPanels.animationDirections.LTR}>
          <TabPanel className="monday-storybook-tabs_bg-color">
            <SignatureBodyContainer>
              <ReactSignatureCanvas
                velocityFilterWeight={1}
                ref={sigRef}
                minWidth={2}
                maxWidth={4}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={() => sigRef.current.handleMouseUp()}
                onEnd={handleCanvasInput}
                clearOnResize={false}
                canvasProps={{
                  className: 'sigCanvas',
                  defaultValue: canvasDataUrl,
                }}
              />
              <CustomButton
                className="delete"
                icon={DeleteIcon}
                kind={Button.kinds.TERTIARY}
                onClick={handleDeleteButtonClick}
              >
                {t('Delete')}
              </CustomButton>
            </SignatureBodyContainer>
            <ActionSectionContainer>
              <CustomButton
                className="add"
                disabled={isAddButtonDisabled}
                onClick={() => {
                  if (sigRef?.current?.isEmpty()) {
                    return triggerToast('Signature pad is empty! Please sign.');
                  }
                  const trimmed = sigRef?.current?.getTrimmedCanvas();
                  handleAdd(trimmed?.toDataURL(), item, 0.25);

                  const newObj = {
                    mode: 'canvas',
                    value: sigRef?.current?.toDataURL(),
                    id: item.title,
                  };
                  setSignatureMode((prevArray) =>
                    comparePrevDataAndUpdate(prevArray, newObj),
                  );
                  handleAddDefaultSignatureOrInitials('canvas');
                }}
              >
                {t('Add')}
              </CustomButton>
            </ActionSectionContainer>
          </TabPanel>
          <TabPanel className="monday-storybook-tabs_bg-color">
            <SignatureBodyContainer>
              <div className="input-container">
                <input
                  type="text"
                  ref={textRef}
                  autoFocus
                  onInput={handleTextInput}
                />
              </div>
              <CustomButton
                className="delete"
                icon={DeleteIcon}
                kind={Button.kinds.TERTIARY}
                onClick={handleDeleteButtonClick}
              >
                {t('Delete')}
              </CustomButton>
            </SignatureBodyContainer>
            <ActionSectionContainer>
              <CustomButton
                className="add"
                disabled={isAddButtonDisabled}
                onClick={() => {
                  convertToImage(item);
                  handleAddDefaultSignatureOrInitials('input');
                }}
              >
                {t('Add')}
              </CustomButton>
            </ActionSectionContainer>
          </TabPanel>
        </TabPanels>
      </TabsContext>
    </SignatureDialogContainer>
  );
};

export default SignatureDialogBody;
